import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { DealbreakerAnswer, DealbreakerAnswerSubmit } from "@/types/dealbreaker";
import { toast } from "sonner";

/**
 * Hook to fetch dealbreaker answers for a specific match
 */
export function useDealbreakerAnswers(matchId: string | undefined, profileId?: string) {
  return useQuery({
    queryKey: ["dealbreaker-answers", matchId, profileId],
    queryFn: async () => {
      if (!matchId) return [];

      let query = supabase
        .from("dealbreaker_answers")
        .select("*")
        .eq("match_id", matchId);

      // If profileId is provided, filter by that as well
      if (profileId) {
        query = query.eq("answered_by_profile_id", profileId);
      }

      const { data, error } = await query;

      if (error) throw error;
      return (data || []) as DealbreakerAnswer[];
    },
    enabled: !!matchId,
  });
}

/**
 * Hook to submit/update dealbreaker answers for a match
 */
export function useSubmitDealbreakerAnswers() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (answers: DealbreakerAnswerSubmit[]) => {
      if (answers.length === 0) {
        throw new Error("No answers to submit");
      }

      // Use upsert to insert or update answers
      const { data, error } = await supabase
        .from("dealbreaker_answers")
        .upsert(
          answers.map((a) => ({
            match_id: a.match_id,
            question_id: a.question_id,
            answer_text: a.answer_text.trim(),
            answered_by_profile_id: a.answered_by_profile_id,
          })),
          {
            onConflict: "match_id,question_id,answered_by_profile_id",
          }
        )
        .select();

      if (error) throw error;
      return (data || []) as DealbreakerAnswer[];
    },
    onSuccess: (data, variables) => {
      const matchId = variables[0]?.match_id;
      const profileId = variables[0]?.answered_by_profile_id;

      // Invalidate and refetch answers for this match
      queryClient.invalidateQueries({
        queryKey: ["dealbreaker-answers", matchId],
      });
      queryClient.invalidateQueries({
        queryKey: ["dealbreaker-answers", matchId, profileId],
      });

      toast.success("Answers submitted successfully");
    },
    onError: (error: any) => {
      console.error("dealbreakers.submit_answers_failed");
      toast.error(error.message || "Failed to submit answers");
    },
  });
}
